import { Wrench } from 'lucide-react';
import { cn } from '@staff-app/ui';
import { safeDistanceToNowStrict } from '@/lib/safe-format';
import type { DeviceAxisState, DeviceConnectivity } from './lib/connectivity';

/** scio3's dot vocabulary — solid emerald online, solid muted offline, hollow ring for "unknown". */
const AXIS_DOT_CLASS: Record<Exclude<DeviceAxisState, 'unavailable'>, string> = {
  online: 'bg-emerald-500',
  offline: 'bg-muted-foreground',
  unknown: 'border border-muted-foreground/50 bg-transparent',
};

const AXIS_TEXT_CLASS: Record<Exclude<DeviceAxisState, 'unavailable'>, string> = {
  online: 'text-emerald-700 dark:text-emerald-400',
  offline: 'text-muted-foreground',
  unknown: 'text-muted-foreground',
};

function AxisRow({
  label,
  state,
  evidence,
  testId,
}: {
  label: string;
  state: DeviceAxisState;
  evidence: string | null;
  testId: string;
}) {
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <dt className="w-16 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="flex min-w-0 items-center gap-2">
        {state === 'unavailable' ? (
          <span className="text-muted-foreground">{'Not available on this platform'}</span>
        ) : (
          <>
            <span
              className={cn('inline-block h-2 w-2 shrink-0 rounded-full', AXIS_DOT_CLASS[state])}
              aria-hidden
            />
            <span className={cn('font-medium', AXIS_TEXT_CLASS[state])}>
              {state === 'online' ? 'Online' : state === 'offline' ? 'Offline' : 'Unknown'}
            </span>
            {evidence && <span className="truncate text-xs text-muted-foreground">{evidence}</span>}
          </>
        )}
      </dd>
    </div>
  );
}

/**
 * Connectivity truth block — port of scio3 `ConnectivitySection`. App row = the player-app heartbeat
 * (prefers the live Redis `liveLastHeartBeat`); Device row = the hardware axis from the agent socket.
 * Room systems (platform-api) render nothing, as in scio3 — their vocabulary is the vendor's.
 *
 * Delta: no "Restart app" button — device actions arrive with the drawer's action bar. The
 * app-not-running state is still called out so staff know a restart is what it needs.
 */
export function ConnectivitySection({
  connectivity,
  appBeat,
  managementCapability,
}: {
  connectivity: DeviceConnectivity;
  appBeat: string | null | undefined;
  managementCapability: string | null | undefined;
}) {
  if (managementCapability === 'platform-api') return null;

  const appAgo = appBeat ? safeDistanceToNowStrict(appBeat, undefined, '') : '';
  const deviceAgo = connectivity.deviceAsOf
    ? safeDistanceToNowStrict(connectivity.deviceAsOf, undefined, '')
    : '';
  const deviceEvidence =
    connectivity.deviceAxis === 'unavailable'
      ? null
      : connectivity.deviceConfidence === 'verified-by-app'
        ? 'confirmed by app heartbeat'
        : deviceAgo
          ? `connected as of ${deviceAgo} ago`
          : null;
  const appNotRunning = !connectivity.appOnline && connectivity.deviceAxis === 'online';

  return (
    <div
      className="rounded-lg border bg-card p-3 shadow-sm"
      data-testid="testing-connectivity-section"
    >
      <dl className="space-y-2 text-sm">
        <AxisRow
          label={'App'}
          state={connectivity.appOnline ? 'online' : 'offline'}
          evidence={appAgo ? `last check-in ${appAgo} ago` : null}
          testId="testing-connectivity-app-row"
        />
        <AxisRow
          label={'Device'}
          state={connectivity.deviceAxis}
          evidence={deviceEvidence}
          testId="testing-connectivity-device-row"
        />
      </dl>

      {connectivity.agentChannelDown && (
        <p
          className="mt-2 text-xs text-muted-foreground"
          data-testid="testing-agent-channel-down-note"
        >
          {'Management channel unavailable — live device info and remote commands may not work.'}
        </p>
      )}

      {appNotRunning && (
        <div
          className="mt-3 rounded-md border border-amber-300/60 bg-amber-50 p-3 dark:border-amber-700/50 dark:bg-amber-950/30"
          data-testid="testing-app-not-running-card"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-400">
            <Wrench className="h-4 w-4 shrink-0" aria-hidden />
            {'App not running'}
          </div>
          <p className="mt-1 text-xs leading-relaxed text-amber-800/90 dark:text-amber-500/90">
            {"The OptiSigns app isn't running, but the device is still connected."}
          </p>
        </div>
      )}
    </div>
  );
}
